import React, { useState, useEffect } from 'react'
import { useTenant } from '../../contexts/TenantContext'
import api, { getStorageUrl } from '../../services/apiService'
import '../../styles/client-admin/compact-player-form.scss'

const CompactPlayerForm = ({ player = null, onSubmit, onCancel, isLoading = false }) => {
  const { tenant } = useTenant()
  const [formData, setFormData] = useState({
    user_detail_id: '',
    full_name: '',
    father_name: '',
    date_of_birth: '',
    gender: 'M',
    blood_group: '',
    height: '',
    weight: '',
    address: '',
    pin_code: '',
    district: '',
    state_name: '',
    position: 'Player',
    aadhar_card: '',
    passport_number: '',
    contact: '',
    mobile: '',
    emergency_contact: '',
    email: '',
    marital_status: '',
    status: 'active'
  })

  const [errors, setErrors] = useState({})
  const [imagePreview, setImagePreview] = useState(null)
  const [imageFile, setImageFile] = useState(null)
  const [users, setUsers] = useState([])
  const [loadingUsers, setLoadingUsers] = useState(false)

  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
    'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
    'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
    'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
    'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Delhi', 'Chandigarh',
    'Jammu and Kashmir', 'Ladakh', 'Puducherry', 'Lakshadweep', 'Andaman and Nicobar Islands'
  ]

  console.log('Indian States loaded:', indianStates.length, 'states')

  const positions = ['Player']

  const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-']

  // Fetch users list on component mount
  useEffect(() => {
    fetchUsers()
  }, [])

  useEffect(() => {
    if (player) {
      setFormData({
        user_detail_id: player.user_detail_id || '',
        full_name: player.fullName || '',
        father_name: player.fatherName || '',
        date_of_birth: player.dob || '',
        gender: player.gender === 'Male' ? 'M' : 'F',
        blood_group: player.bloodGroup || '',
        height: player.height || '',
        weight: player.weight || '',
        address: player.address || '',
        pin_code: player.pinCode || '',
        district: player.district || '',
        state_name: player.state || '',
        position: player.position || 'Player',
        aadhar_card: player.aadharCard || '',
        passport_number: player.passportNum || '',
        contact: player.contact || '',
        mobile: player.mobile || '',
        emergency_contact: player.emergencyContact || '',
        email: player.email || '',
        marital_status: player.maritalStatus || '',
        status: player.status === 'Active' ? 'active' : 'inactive'
      })

      if (player.image) {
        setImagePreview(getStorageUrl(player.image))
      }
    }
  }, [player])

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true)
      const response = await api.get('/client-admin/users')
      if (response.data.success) {
        setUsers(response.data.data || [])
      }
    } catch (error) {
      console.error('Error fetching users:', error)
    } finally {
      setLoadingUsers(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, image: 'Image size should be less than 2MB' }))
        return
      }

      setImageFile(file)
      const reader = new FileReader()
      reader.onload = (e) => {
        setImagePreview(e.target.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.user_detail_id) newErrors.user_detail_id = 'Please select a user for this player'
    if (!formData.full_name.trim()) newErrors.full_name = 'Player name is required'
    if (!formData.father_name.trim()) newErrors.father_name = "Father's name is required"
    if (!formData.date_of_birth) newErrors.date_of_birth = 'Date of birth is required'
    if (!formData.state_name) newErrors.state_name = 'State is required'
    if (!formData.gender) newErrors.gender = 'Gender is required'

    // Validate email format if provided
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format'
    }

    // Validate phone number format if provided
    if (formData.contact && !/^\d{10}$/.test(formData.contact)) {
      newErrors.contact = 'Contact must be 10 digits'
    }

    // Validate Aadhar format if provided
    if (formData.aadhar_card && !/^\d{12}$/.test(formData.aadhar_card)) {
      newErrors.aadhar_card = 'Aadhar must be 12 digits'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!validateForm()) return

    const submitData = { ...formData }

    onSubmit(submitData, imageFile)
  }

  const calculateAge = () => {
    if (!formData.date_of_birth) return null
    const birthDate = new Date(formData.date_of_birth)
    const today = new Date()
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--
    }
    return age
  }

  const getAgeCategory = (age) => {
    if (!age) return ''
    if (age <= 12) return 'U-12 (Little League)'
    if (age <= 15) return 'U-15 (Sub Junior)'
    if (age <= 18) return 'U-18 (Junior)'
    return 'Senior'
  }

  const currentAge = calculateAge()

  return (
    <div className="compact-player-form">
      <div className="form-header">
        <h2>
          <i className="fas fa-user-plus"></i>
          {player ? 'Edit Player' : 'Register New Player'}
        </h2>
        <button className="btn-close" onClick={onCancel}>
          <i className="fas fa-times"></i>
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-body">
          {/* Image Upload Section */}
          <div className="image-upload-section">
            <div className="image-preview">
              {imagePreview ? (
                <img src={imagePreview} alt="Player" />
              ) : (
                <div className="placeholder">
                  <i className="fas fa-user-circle"></i>
                  <p>Upload Photo</p>
                </div>
              )}
            </div>
            <div className="image-upload-controls">
              <label className="btn-upload">
                <i className="fas fa-camera"></i> Choose Photo
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: 'none' }}
                />
              </label>
              {errors.image && <span className="error-text">{errors.image}</span>}
              <small>JPG, PNG, GIF (Max 2MB)</small>
            </div>
          </div>

          {/* Personal Information */}
          <div className="form-section">
            <h3><i className="fas fa-user"></i> Personal Information</h3>
            <div className="form-grid">
              <div className="form-group full-width">
                <label>Select User (Player Owner) <span className="required">*</span></label>
                <select
                  name="user_detail_id"
                  value={formData.user_detail_id}
                  onChange={handleChange}
                  className={errors.user_detail_id ? 'error' : ''}
                  disabled={loadingUsers}
                >
                  <option value="">
                    {loadingUsers ? 'Loading users...' : 'Select user for this player'}
                  </option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.full_name} ({user.username}) - {user.email}
                    </option>
                  ))}
                </select>
                {errors.user_detail_id && <span className="error-text">{errors.user_detail_id}</span>}
                <small className="info-text">
                  <i className="fas fa-info-circle"></i> This player will be linked to the selected user's account
                </small>
              </div>

              <div className="form-group">
                <label>Full Name <span className="required">*</span></label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  placeholder="Enter player's full name"
                  className={errors.full_name ? 'error' : ''}
                />
                {errors.full_name && <span className="error-text">{errors.full_name}</span>}
              </div>

              <div className="form-group">
                <label>Father's Name <span className="required">*</span></label>
                <input
                  type="text"
                  name="father_name"
                  value={formData.father_name}
                  onChange={handleChange}
                  placeholder="Enter father's name"
                  className={errors.father_name ? 'error' : ''}
                />
                {errors.father_name && <span className="error-text">{errors.father_name}</span>}
              </div>

              <div className="form-group">
                <label>Date of Birth <span className="required">*</span></label>
                <input
                  type="date"
                  name="date_of_birth"
                  value={formData.date_of_birth}
                  onChange={handleChange}
                  max={new Date().toISOString().split('T')[0]}
                  className={errors.date_of_birth ? 'error' : ''}
                />
                {errors.date_of_birth && <span className="error-text">{errors.date_of_birth}</span>}
                {currentAge && (
                  <small className="info-text">
                    Age: {currentAge} years ({getAgeCategory(currentAge)})
                  </small>
                )}
              </div>

              <div className="form-group">
                <label>Gender <span className="required">*</span></label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  className={errors.gender ? 'error' : ''}
                >
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                </select>
                {errors.gender && <span className="error-text">{errors.gender}</span>}
              </div>

              <div className="form-group">
                <label>Blood Group</label>
                <select
                  name="blood_group"
                  value={formData.blood_group}
                  onChange={handleChange}
                >
                  <option value="">Select Blood Group</option>
                  {bloodGroups.map(bg => (
                    <option key={bg} value={bg}>{bg}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Height (cm)</label>
                <input
                  type="number"
                  name="height"
                  value={formData.height}
                  onChange={handleChange}
                  placeholder="Height in cm"
                  step="0.1"
                />
              </div>

              <div className="form-group">
                <label>Weight (kg)</label>
                <input
                  type="number"
                  name="weight"
                  value={formData.weight}
                  onChange={handleChange}
                  placeholder="Weight in kg"
                  step="0.1"
                />
              </div>

              <div className="form-group">
                <label>Marital Status</label>
                <select
                  name="marital_status"
                  value={formData.marital_status}
                  onChange={handleChange}
                >
                  <option value="">Select Status</option>
                  <option value="Single">Single</option>
                  <option value="Married">Married</option>
                </select>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="form-section">
            <h3><i className="fas fa-phone"></i> Contact Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Contact Number</label>
                <input
                  type="tel"
                  name="contact"
                  value={formData.contact}
                  onChange={handleChange}
                  placeholder="10-digit contact number"
                  maxLength="10"
                  className={errors.contact ? 'error' : ''}
                />
                {errors.contact && <span className="error-text">{errors.contact}</span>}
              </div>

              <div className="form-group">
                <label>Mobile Number</label>
                <input
                  type="tel"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  placeholder="10-digit mobile number"
                  maxLength="10"
                />
              </div>

              <div className="form-group">
                <label>Emergency Contact</label>
                <input
                  type="tel"
                  name="emergency_contact"
                  value={formData.emergency_contact}
                  onChange={handleChange}
                  placeholder="Emergency contact number"
                  maxLength="10"
                />
              </div>

              <div className="form-group full-width">
                <label>Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="player@example.com"
                  className={errors.email ? 'error' : ''}
                />
                {errors.email && <span className="error-text">{errors.email}</span>}
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="form-section">
            <h3><i className="fas fa-map-marker-alt"></i> Address Information</h3>
            <div className="form-grid">
              <div className="form-group full-width">
                <label>Address</label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Enter complete address"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>District</label>
                <input
                  type="text"
                  name="district"
                  value={formData.district}
                  onChange={handleChange}
                  placeholder="District"
                />
              </div>

              <div className="form-group">
                <label>State <span className="required">*</span></label>
                <select
                  name="state_name"
                  value={formData.state_name}
                  onChange={handleChange}
                  className={errors.state_name ? 'error' : ''}
                >
                  <option value="">Select State</option>
                  {indianStates.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
                {errors.state_name && <span className="error-text">{errors.state_name}</span>}
              </div>

              <div className="form-group">
                <label>PIN Code</label>
                <input
                  type="text"
                  name="pin_code"
                  value={formData.pin_code}
                  onChange={handleChange}
                  placeholder="6-digit PIN code"
                  maxLength="6"
                />
              </div>
            </div>
          </div>

          {/* Playing Information */}
          <div className="form-section">
            <h3><i className="fas fa-baseball-ball"></i> Playing Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Position</label>
                <select
                  name="position"
                  value={formData.position}
                  onChange={handleChange}
                >
                  {positions.map(pos => (
                    <option key={pos} value={pos}>{pos}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Identity Information */}
          <div className="form-section">
            <h3><i className="fas fa-id-card"></i> Identity Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Aadhar Card Number</label>
                <input
                  type="text"
                  name="aadhar_card"
                  value={formData.aadhar_card}
                  onChange={handleChange}
                  placeholder="12-digit Aadhar number"
                  maxLength="12"
                  className={errors.aadhar_card ? 'error' : ''}
                />
                {errors.aadhar_card && <span className="error-text">{errors.aadhar_card}</span>}
              </div>

              <div className="form-group">
                <label>Passport Number</label>
                <input
                  type="text"
                  name="passport_number"
                  value={formData.passport_number}
                  onChange={handleChange}
                  placeholder="Passport number (if available)"
                  maxLength="20"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="form-footer">
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={isLoading}>
            <i className="fas fa-times"></i> Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={isLoading}>
            {isLoading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> {player ? 'Updating...' : 'Registering...'}
              </>
            ) : (
              <>
                <i className="fas fa-save"></i> {player ? 'Update Player' : 'Register Player'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default CompactPlayerForm